import { getIdNamePairById } from "@/methods/utils";
import { useParentAffiliationStore } from "@/stores/affiliation/parentAffiliationStore";
import { useApiServicesStore } from "@/stores/core/apiServicesStore";
import { AffiliationStore } from "@/types/affiliation";
import { CourseSubjectMappingDisplay } from "@/types/content";
import { defineStore, storeToRefs } from "pinia";

function getInitialAffiliationStore(): AffiliationStore {
  const initialAffiliationStore: AffiliationStore = {
    SelectedBatch: null,
    SelectedSubject: null,
  };
  return JSON.parse(JSON.stringify(initialAffiliationStore));
}

export const useAffiliationStore = defineStore("affiliationStore", {
  state: (): AffiliationStore => getInitialAffiliationStore(),
  actions: {
    async updateSelectedBatch(batchId: number) {
      const allBatches = await this.getBatchesForAffiliation();
      const matchedBatch = getIdNamePairById(allBatches, batchId);
      if (!matchedBatch) {
        return;
      }
      this.SelectedBatch = matchedBatch;
    },
    async getSubjectsForBatch(batchId: number, allSubjectsTitle: string | null) {
      const batchSubjectOptions = await useParentAffiliationStore().getSubjectsForBatch(
        batchId,
        allSubjectsTitle,
      );
      const matchingSubject = batchSubjectOptions.find((s) => s.Id === this.SelectedSubject?.Id);
      if (matchingSubject === undefined) {
        this.SelectedSubject = null;
      }
      return batchSubjectOptions;
    },
    // get-subjects-for-batch returns MASTER SubjectIds, but the content-v2 surface keys on the
    // Course↔Subject junction — resolve a picked subject to its CourseSubjectId before calling any
    // endpoint whose filter is a courseSubjectId. Returns null when the subject isn't mapped.
    async getCourseSubjectIdForBatchSubject(
      batchId: number,
      subjectId: number,
    ): Promise<number | null> {
      const batchInfo = await this.getAffiliationCourseForBatch(batchId);
      if (!batchInfo) {
        return null;
      }
      const apiServicesStore = useApiServicesStore();
      const { contentReadApi } = storeToRefs(apiServicesStore);
      const response = await contentReadApi.value.getCourseSubjectMappings({
        CourseIds: [batchInfo.CourseId],
      });
      if (!response.isSuccess) {
        return null;
      }
      const matchedMapping = (response.data as Array<CourseSubjectMappingDisplay>).find(
        (mapping) => mapping.SubjectId === subjectId,
      );
      return matchedMapping ? matchedMapping.CourseSubjectId : null;
    },
    async getAffiliationCourseForBatch(batchId: number) {
      return await useParentAffiliationStore().getAffiliationCourseForForBatch(batchId);
    },
    async getAffiliationStatistics(affiliationId: number) {
      return await useParentAffiliationStore().getAffiliationStatistics(affiliationId);
    },
    async getCoursesForAffiliation(affiliationId: number) {
      return await useParentAffiliationStore().getCoursesForAffiliation(affiliationId);
    },
    async getBatchesForAffiliation(affiliationId: number | null = null) {
      return await useParentAffiliationStore().getBatchesForAffiliationCourse(
        false,
        null,
        affiliationId,
      );
    },
  },
});
